/** Deduplicate work without letting one subscriber cancel another subscriber. */
export function sharedRequests<T>() {
  const pending = new Map<string, { controller: AbortController; promise: Promise<T>; users: number }>();
  return (key: string, run: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (signal?.aborted) return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    let entry = pending.get(key);
    if (!entry) {
      const controller = new AbortController();
      entry = { controller, promise: Promise.resolve().then(() => { controller.signal.throwIfAborted(); return run(controller.signal); }), users: 0 };
      pending.set(key, entry);
      const owned = entry;
      const clear = () => { if (pending.get(key) === owned) pending.delete(key); };
      // Two explicit handlers avoid an unobserved rejected finally() promise.
      void entry.promise.then(clear, clear);
    }
    const owned = entry;
    owned.users += 1;
    return new Promise<T>((resolve, reject) => {
      let finished = false;
      const finish = (accept: () => void) => {
        if (finished) return;
        finished = true;
        signal?.removeEventListener('abort', abort);
        owned.users -= 1;
        if (owned.users === 0 && pending.get(key) === owned) {
          pending.delete(key);
          owned.controller.abort();
        }
        accept();
      };
      const abort = () => finish(() => reject(signal?.reason ?? new DOMException('Aborted', 'AbortError')));
      signal?.addEventListener('abort', abort, { once: true });
      void owned.promise.then(value => finish(() => resolve(value)), error => finish(() => reject(error)));
    });
  };
}
